// components/LDProvider.tsx
'use client'

import { LDProvider as _LDProvider } from 'launchdarkly-react-client-sdk'
import type { LDContext, LDLogger } from 'launchdarkly-js-client-sdk'
import Observability from '@launchdarkly/observability'
import SessionReplay from '@launchdarkly/session-replay'
import type { ReactNode } from 'react'
import { useMemo } from 'react'

interface Props {
  clientSideID: string
  bootstrap: Record<string, unknown>
  context: Record<string, unknown>  // compatible with LDContext JSON shape
  environment: string
  serviceVersion: string
  children: ReactNode
}

/**
 * CORRECTION to the original commit message (b3a6f89d): that claimed the
 * browser SDK routes every level to console.error. It does not. Only the NODE
 * SDK behaves that way, which is what lib/launchdarkly/server.ts fixes.
 * launchdarkly-js-sdk-common builds per-level destinations
 * ([toConsole('log'), toConsole('info'), toConsole('warn'), toConsole('error')]),
 * so an info line reaches console.info and Playwright types it 'info', never
 * 'error'. This change therefore did NOT cause the public-page
 * "renders without console errors" failures; a CSP connect-src violation did
 * (see #684), and the browser emits those as genuine console errors.
 *
 * Kept anyway as a deliberate improvement: it quietens debug and info chatter
 * outside development, matching the server-side policy, and suppresses nothing
 * because error still routes to console.error.
 */
function ldClientLogger(): LDLogger {
  // A plain LDLogger rather than basicLogger: the BROWSER sdk's basicLogger
  // takes `destination` as a single (line: string) => void, so it cannot route
  // per level. Only the Node sdk accepts the per-level object that
  // lib/launchdarkly/server.ts uses.
  const verbose = process.env.NODE_ENV === 'development'
  return {
    debug: (...args: unknown[]) => { if (verbose) console.log(...args) },
    info: (...args: unknown[]) => { if (verbose) console.log(...args) },
    warn: (...args: unknown[]) => console.warn(...args),
    error: (...args: unknown[]) => console.error(...args),
  }
}

export function LDProvider({ clientSideID, bootstrap, context, environment, serviceVersion, children }: Props) {
  // Plugins are instantiated once per mount (useMemo with empty deps).
  // 'strict' privacy setting masks all text/inputs in session replay — important
  // for a platform handling family and children data.
  const plugins = useMemo(() => [
    new Observability({
      serviceName: 'aupairly-web',
      version: serviceVersion,
      environment,
      // Uncaught errors remain automatic. Console records can contain arbitrary
      // application data and are not part of the aggregate evidence contract.
      disableConsoleRecording: true,
      reportConsoleErrors: false,
      productAnalytics: false,
      // #899: performance recording runs a jank tracker that throws on
      // targets without an `id` (`e.id.length`, unfixed in 1.2.1; Sentry
      // AUPAIRLY-WEB-7) and groups gauges by the full page URL, query string
      // included. Sentry covers performance now, under its data allowlist.
      // (SessionReplay has no such tracker and no such option.)
      enablePerformanceRecording: false,
      networkRecording: {
        enabled: true,
        // Timings only. Bodies and headers on this platform carry family and
        // children's data plus Supabase auth headers, which we will not ship to
        // a third-party observability store (POPIA).
        recordHeadersAndBody: false,
      },
    }),
    new SessionReplay({
      privacySetting: 'strict',
    }),
  ], [environment, serviceVersion])

  // Built once per mount alongside the plugins, for the same reason.
  const logger = useMemo(() => ldClientLogger(), [])

  // #684 — streaming: false because streaming has never actually worked here
  // (CSP blocked the connection since day one); this makes the code honest
  // about that rather than claiming live updates that never landed. Interim
  // fix (option 2) — migrating off the 311 networkidle e2e call sites that a
  // live EventSource would break is option 1, the real long-term fix.
  return (
    <_LDProvider
      clientSideID={clientSideID}
      context={context as unknown as LDContext}
      options={
        { bootstrap, streaming: false, plugins, logger } as object
      }
    >
      {children}
    </_LDProvider>
  )
}
